import {
  CalendarOutlined,
  CopyOutlined,
  EditOutlined,
  FileOutlined,
  LoadingOutlined,
  PlusOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { debounce, startCase } from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';

import TableFilterAdvanced from '../../components/TableFilterAdvanced';
import TableFilterCheckbox from '../../components/TableFilterCheckbox';
import TableFilterDateRange from '../../components/TableFilterDateRange';
import TableFilterSearch, {
  filterSearchIcon,
} from '../../components/TableFilterSearch';
import { URL_TICKETING_SUBMIT } from '../../constants/URLS';
import { useSAUsers, useUserRegions } from '../../custom-hooks/sa-users';
import { fetchAPI } from '../../utils/api';
import { formatUTCTimeToLocal } from '../../utils/format';
import { safelyParseJSON } from '../../utils/json_utils';
import AdditionalQuestionAnswers from './SubmittedTicketAdditionalQuestions';
import RFISupplierEmailButton from './RFISupplierEmailButton';
import SubmitTicketColumns, {
  defaultSubmittedTicketableColumns,
} from './SubmittedTicketColumns';
import SubmittedTicketHistory from './SubmittedTicketHistory';
import SubmitTicketSupplierDropdown from './SubmitTicketSupplierDropdown';
import { RFIStatusColor, RFITicket, RFITicketType } from './types';
import { FIVE_MINUTES } from '../../constants/CONSTANTS';
import { queryClient } from '../../QueryClient';

const SubmittedTicketList = () => {
  const [queryParams, setQueryParams] = useQueryParams({
    tab: withDefault(StringParam, 'list'),
    page: withDefault(NumberParam, 1),
    per_page: withDefault(NumberParam, 10),
    filters: withDefault(StringParam, '{}'),
    sorter: withDefault(StringParam, '{}'),
  });
  const user = useSelector((state: AppState) => state.user);
  const filtersObject = safelyParseJSON(queryParams.filters);
  const sorterObject = safelyParseJSON(queryParams.sorter);
  const userRegions = useUserRegions();
  const saUsers = useSAUsers(user);
  const [tableColumns, setTableColumns] = useState(
    defaultSubmittedTicketableColumns
  );
  const [selectedMyTickets, setSelectedMyTickets] = useState(true);
  const tickets = useQuery<{
    items: RFITicket[];
    filters?: any;
    page: number;
    per_page: number;
    total: number;
    loading: boolean;
  }>(
    ['submitted-tickets', queryParams, selectedMyTickets],
    async () => {
      if (queryParams.tab !== 'list') {
        return {
          items: [],
          filters: {},
          page: queryParams.page,
          per_page: queryParams.per_page,
          total: 0,
          loading: true,
        };
      }
      try {
        const sorterPayload = {
          columnKey: 'created_at',
          order: 'descend',
        };
        if (!sorterObject?.columnKey || !sorterObject?.order) {
          sorterPayload.columnKey = 'created_at';
          sorterPayload.order = 'descend';
        }
        if (selectedMyTickets) {
          filtersObject.action_by_id = [user.id];
        }
        const resp = await fetchAPI('rfi-ticketing/list', {
          method: 'POST',
          payload: {
            page: queryParams.page,
            per_page: queryParams.per_page,
            filters: filtersObject,
            sorter: sorterPayload,
          },
        });

        return { ...resp, loading: false };
      } catch (error) {
        message.error('Failed get submitted tickets');
        if (error.message) {
          message.error(error.message);
        }

        return {
          items: [],
          filters: {},
          page: queryParams.page,
          per_page: queryParams.per_page,
          total: 0,
          loading: false,
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        items: [],
        filters: {},
        page: queryParams.page,
        per_page: queryParams.per_page,
        total: 0,
        loading: true,
      },
    }
  );

  const statusSummary = useQuery<{
    status_count: object;
  }>(
    ['rfi-status-summary', selectedMyTickets, queryParams.filters],
    async () => {
      try {
        if (selectedMyTickets) {
          filtersObject.action_by_id = [user.id];
        }
        const resp = await fetchAPI('rfi-ticketing/status-summary', {
          method: 'POST',
          payload: {
            action_by_id: selectedMyTickets
              ? [user.id]
              : filtersObject.action_by_id,
          },
        });

        return resp;
      } catch (error) {
        return {};
      }
    },
    {
      staleTime: FIVE_MINUTES,
      placeholderData: {
        status_count: {},
      },
    }
  );

  useEffect(() => {
    const savedColumns = localStorage.getItem('rfi-ticketing-table-colums');
    if (savedColumns) {
      try {
        const parsedSavedColumns = JSON.parse(savedColumns);
        setTableColumns(parsedSavedColumns);
      } catch (error) {
        localStorage.removeItem('rfi-ticketing-table-colums');
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Wrapper>
      <Row justify="space-between">
        <Col span={12}>
          <Row className="top-btn-container">
            <Col>
              <Button
                size="small"
                disabled={!filtersObject || !Object.keys(filtersObject).length}
                onClick={() => setQueryParams({ filters: '{}' })}
              >
                Reset Filter
              </Button>
            </Col>
            <Col>
              <SubmitTicketColumns
                columns={tableColumns}
                setColumns={payload => {
                  setTableColumns(payload);
                  localStorage.setItem(
                    'rfi-ticketing-table-colums',
                    JSON.stringify(payload)
                  );
                }}
              />
            </Col>
            <Col>
              <Checkbox
                checked={selectedMyTickets}
                onChange={e => setSelectedMyTickets(e.target.checked)}
              >
                My Tickets
              </Checkbox>
            </Col>
            <Col>
              <Link to={URL_TICKETING_SUBMIT}>
                <Button type="primary" size="small" icon={<PlusOutlined />}>
                  Create RFI Ticket
                </Button>
              </Link>
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <Row justify="end" align="middle">
            {Object.keys(statusSummary.data.status_count || {}).map(key => (
              <Tag
                color={RFIStatusColor[key]?.bg}
                style={{ color: RFIStatusColor[key]?.text }}
              >
                {key} : {statusSummary.data.status_count[key]}
              </Tag>
            ))}
            <Button
              loading={statusSummary.isFetching}
              shape="circle"
              icon={<RedoOutlined />}
              onClick={() => {
                queryClient.invalidateQueries({
                  predicate: query =>
                    query.queryKey[0] === 'users' ||
                    query.queryKey[0] === 'rfi-status-summary',
                });
              }}
            />
          </Row>
        </Col>
      </Row>

      <Table
        bordered
        dataSource={tickets.data.items || []}
        scroll={{ x: 140, y: 'calc(100vh - 180px)' }}
        loading={tickets.isLoading || tickets.data.loading}
        columns={[
          {
            title: 'Ticket Number',
            dataIndex: 'rfi_number',
            key: 'rfinumber',
            width: 200,
            fixed: 'left',
            filterDropdown: TableFilterSearch,
            filterIcon: filterSearchIcon,
            defaultFilteredValue: filtersObject['rfinumber'],
            render: (text, record) => {
              return (
                <Space>
                  <span className={record.is_over_due ? 'overdue-row' : ''}>
                    {text}
                  </span>
                  <SubmittedTicketHistory record={record} />
                  <CopyToClipboard
                    onCopy={() => message.success(`Copied ${text}`)}
                    text={text}
                  >
                    <Button shape="circle" icon={<CopyOutlined />} />
                  </CopyToClipboard>
                </Space>
              );
            },
          },
          ...[
            {
              title: 'Type of Issue',
              width: 195,
              key: 'rfitype',
              render: (text, record) => {
                return <RFITypeSelect record={record} />;
              },
              filters: [
                { text: 'RIC', value: 'RIC' },
                { text: 'CIC', value: 'CIC' },
                { text: 'FIC', value: 'FIC' },
                { text: 'HIC', value: 'HIC' },
                { text: 'EIC', value: 'EIC' },
                { text: 'PIC', value: 'PIC' },
                { text: 'TIC', value: 'TIC' },
                { text: 'NOT SET', value: 'NOT_SET' },
                { text: 'GRN DISPUTE', value: 'GRN_DISPUTE' },
              ],
              filterMultiple: true,
              defaultFilteredValue: filtersObject['rfitype'],
            },
            {
              title: 'Date of Resolution',
              width: 175,
              key: 'resolution_date',
              filterDropdown: TableFilterDateRange,
              render: (text, record) => {
                return (
                  <RFIDatepicker
                    record={record}
                    fieldKey="resolution_date"
                    placeholder="Select Date"
                  />
                );
              },
              defaultFilteredValue: filtersObject['resolution_date'],
              sorter: true,
            },
            {
              title: 'Revised Deadline',
              width: 175,
              key: 'revised_dateline',
              filterDropdown: TableFilterDateRange,
              render: (text, record) => {
                // return '25 Dec 2000 (dummy)';
                return (
                  <RFIDatepicker
                    record={record}
                    fieldKey="revised_dateline"
                    placeholder="Select Date"
                  />
                );
              },
              defaultFilteredValue: filtersObject['revised_dateline'],
              sorter: true,
            },
            {
              title: 'Assigned Staff',
              width: 150,
              key: 'action_by_id',
              filterDropdown: TableFilterCheckbox({
                options: [
                  { text: 'Unassigned', value: 'Unassigned' },
                  ...saUsers.data.list.map(user => ({
                    text: user.full_name,
                    value: user.id,
                  })),
                ],
                searchPlaceholder: 'Find Assignee',
              }),
              render: (text, record) => {
                return <RFIActionBy record={record} />;
              },
              defaultFilteredValue: filtersObject['action_by_id'],
            },
            {
              title: 'Status',
              width: 180,
              key: 'status',
              filters: [
                'Submitted',
                'Assigned',
                'Acknowledged',
                'Resolved',
                'InvoiceDisputed',
                'ProcessingInvoice',
                'InvoicePaid',
                'InvoiceRejected',
                'RFQInProgress',
                'RFQRejected',
                'RFQSuccess',
                'RFQInDispute',
                'Closed',
              ].map(str => ({
                text: (
                  <Tag
                    color={RFIStatusColor[str]?.bg}
                    style={{ color: RFIStatusColor[str]?.text }}
                  >
                    {str}
                  </Tag>
                ),
                value: str,
              })),
              filterMultiple: true,
              render: (text, record) => {
                return <RFITicketStatus record={record} />;
              },
              defaultFilteredValue: filtersObject['status'],
            },
            {
              title: 'Buyer Entity',
              width: 150,
              render: (text, record) => {
                return <RFIBuyerEntitySelect record={record} />;
              },
              filterDropdown: TableFilterAdvanced({
                endpoint: 'entity/list-for-filter',
                method: 'POST',
                errorMsg: 'Failed get buyer entities',
                searchPlaceholder: 'Find Buyer Entity',
                parser: {
                  value: item => String(item.id),
                  label: item => <span>{item.name}</span>,
                },
              }),
              key: 'entity_id',
              defaultFilteredValue: filtersObject['entity_id'],
            },
            {
              title: 'Region',
              key: 'region',
              width: 100,
              filters: userRegions.map(item => ({ text: item, value: item })),
              render: (text, record) => {
                return record.is_all_region ? 'All Region' : record.region;
              },
              defaultFilteredValue: filtersObject?.region,
            },
            {
              title: 'Company',
              width: 175,
              key: 'company',
              render: (text, record) => {
                return (
                  <div className="company">
                    <p className="name">{record.requester_company_name}</p>
                    <p className="email">
                      <i>{record.requester_company_email}</i>
                    </p>
                  </div>
                );
              },
              filterDropdown: TableFilterSearch,
              filterIcon: filterSearchIcon,
              defaultFilteredValue: filtersObject?.company,
            },
            {
              title: 'Timestamp',
              width: 175,
              key: 'created_at',
              filterDropdown: TableFilterDateRange,
              defaultFilteredValue: filtersObject?.created_at,
              render: (text, record) => {
                return formatUTCTimeToLocal(
                  record.created_at,
                  'DD MMM YYYY HH:mm'
                );
              },
              sorter: true,
            },
            {
              title: 'Requestor',
              width: 175,
              key: 'requestor',
              render: (text, record) => {
                return (
                  <div className="company">
                    <p className="name">{record.requester_name}</p>
                    <p className="email">
                      <i>{record.requester_email}</i>
                    </p>
                  </div>
                );
              },
              filterDropdown: TableFilterSearch,
              filterIcon: filterSearchIcon,
              defaultFilteredValue: filtersObject?.requestor,
            },
            {
              title: 'Deadline',
              width: 175,
              key: 'date_needed_by',
              filterDropdown: TableFilterDateRange,
              defaultFilteredValue: filtersObject?.date_needed_by,
              render: (text, record) => {
                return formatUTCTimeToLocal(
                  record.date_needed_by,
                  'DD MMM YYYY HH:mm'
                );
              },
              sorter: true,
            },
            {
              title: 'Request Message',
              width: 250,
              key: 'request_msg',
              render: (text, record) => {
                return (
                  <>
                    <RFIRequestMsg msg={record.request_msg} />
                    {!!record.qna_items?.length && (
                      <AdditionalQuestionAnswers items={record.qna_items} />
                    )}
                  </>
                );
              },
              filterDropdown: TableFilterSearch,
              filterIcon: filterSearchIcon,
              defaultFilteredValue: filtersObject?.request_msg,
            },
            {
              title: 'PO Number',
              width: 160,
              key: 'po_number',
              render: (text, record) => {
                return record.po_number || '-';
              },
              filterDropdown: TableFilterSearch,
              filterIcon: filterSearchIcon,
              defaultFilteredValue: filtersObject?.po_number,
            },
            {
              title: 'Supplier',
              width: 220,
              key: 'supplier_id',
              render: (text, record) => {
                return <RFISupplierSelect record={record} />;
              },
              filterDropdown: TableFilterAdvanced({
                endpoint: 'suppliers/list-lite',
                method: 'POST',
                errorMsg: 'Failed get supplier list',
                searchPlaceholder: 'Find Supplier',
                parser: {
                  value: item => String(item.id),
                  label: item => (
                    <span>
                      {item.name} <i>({item.id})</i>
                    </span>
                  ),
                },
              }),
              filterIcon: filterSearchIcon,
              defaultFilteredValue: filtersObject?.supplier_id,
            },
            {
              title: 'Attachments',
              key: 'attachments',
              width: 200,
              render: (text, record) => {
                if (!record.rfi_upload_files.length) {
                  return '-';
                }

                return (
                  <div className="attachments">
                    {record.rfi_upload_files.map(attFile => (
                      <RFIAttachment
                        id={attFile.id}
                        name={attFile.name}
                        url={attFile.url}
                        key={attFile.id}
                        uploadedToSME={
                          !!attFile?.additional_fields?.uploaded_to_sme
                        }
                      />
                    ))}
                  </div>
                );
              },
            },
            {
              title: 'Note',
              width: 200,
              key: 'comment',
              render: (text, record) => {
                return <RFINote record={record} />;
              },
              filterDropdown: TableFilterSearch,
              filterIcon: filterSearchIcon,
              defaultFilteredValue: filtersObject?.comment,
            },
          ].reduce((arr, obj) => {
            const setting = tableColumns.find(i => i.key === obj.key);
            // column not found ??
            if (!setting) {
              return [...arr, obj];
            }

            // column is hidden
            if (!setting.active) {
              return arr;
            }

            // sort columns based on order on tableColumns
            const mergedColumns = [...arr, obj];
            mergedColumns.sort((a, b) => {
              const aIdx = tableColumns.findIndex(i => i.key === a.key);
              const bIdx = tableColumns.findIndex(i => i.key === b.key);

              if (aIdx > bIdx) {
                return 1;
              } else if (aIdx < bIdx) {
                return -1;
              } else {
                return 0;
              }
            });

            return mergedColumns;
          }, []),
        ]}
        pagination={{
          current: queryParams.page,
          pageSize: queryParams.per_page,
          total: tickets.data.total || 0,
          onChange: (page, pageSize) =>
            setQueryParams({
              page: pageSize !== queryParams.per_page ? 1 : page,
              per_page: pageSize,
            }),
          showSizeChanger: true,
          size: 'small',
        }}
        onChange={(pagination, filters, sorter) => {
          const trimmedFilters = Object.keys(filters).reduce((obj, key) => {
            if (filters[key]) {
              return { ...obj, [key]: filters[key] };
            }

            return obj;
          }, {});
          setQueryParams({
            filters: JSON.stringify(trimmedFilters),
            sorter: JSON.stringify(sorter),
          });
        }}
      />
    </Wrapper>
  );
};

const RFITypeSelect = (props: { record: RFITicket }) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    real: props.record.rfi_type,
    temp: props.record.rfi_type,
  });

  const changeRFIType = async (value: RFITicketType) => {
    setValues(v => ({ ...v, real: value }));
    setLoading(true);
    try {
      await fetchAPI('rfi-ticketing/update', {
        method: 'PATCH',
        version: '2.0',
        payload: {
          id: props.record.id,
          rfi_type: value,
        },
      });
      setValues(v => ({ ...v, temp: value }));
    } catch (error) {
      message.error('Failed change RFI Type');
      setValues(v => ({ ...v, real: v.temp }));
    }
    setLoading(false);
  };

  return (
    <Select
      defaultValue={values.real}
      style={{ width: '100%' }}
      onChange={val => changeRFIType(val)}
      loading={loading}
    >
      {[
        'RIC',
        'CIC',
        'FIC',
        'HIC',
        'EIC',
        'PIC',
        'TIC',
        'NOT_SET',
        'GRN_DISPUTE',
      ].map((str, idx) => (
        <Select.Option value={str} key={`${str}-${idx}`}>
          {startCase(str)}
        </Select.Option>
      ))}
    </Select>
  );
};

export const RFIAttachment = (props: {
  id: number;
  name: string;
  url: string;
  uploadedToSME?: boolean;
}) => {
  const [downloading, setDownloading] = useState(false);

  const startDownload = async () => {
    setDownloading(true);
    if (props.uploadedToSME) {
      window.open(props.url, '_blank');
      setDownloading(false);
      return;
    }

    try {
      const resp = await fetchAPI(`upload/${props.id}`);
      window.open(resp.url, '_blank');
    } catch (error) {
      message.error('Failed download attachment');
      if (error.message) {
        message.error(error.message);
      }
    }
    setDownloading(false);
  };

  return (
    <div
      className="attachment-card"
      onClick={() => !downloading && startDownload()}
    >
      {downloading ? <LoadingOutlined /> : <FileOutlined />} {props.name}
    </div>
  );
};

const updateRFITicket = payload => {
  return fetchAPI('rfi-ticketing/update', {
    method: 'PATCH',
    version: '2.0',
    payload,
  });
};

const RFISupplierSelect = (props: { record: RFITicket }) => {
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<{
    real?: number;
    temp?: number;
  }>({
    real: props.record.supplier_id,
    temp: props.record.supplier_id,
  });

  useEffect(() => {
    setValue({
      real: props.record.supplier_id,
      temp: props.record.supplier_id,
    });
  }, [props.record?.supplier_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeRFISupplier = async (supplierId?: number) => {
    setValue(current => ({ ...current, temp: supplierId }));
    setLoading(true);
    try {
      await updateRFITicket({
        id: props.record.id,
        supplier_id: supplierId || null,
      });
      setValue({ real: supplierId, temp: supplierId });
      queryClient.invalidateQueries({
        predicate: query => query.queryKey[0] === 'submitted-tickets',
      });
    } catch (error) {
      message.error('Failed change supplier');
      message.error(error.message || 'Error');
      setValue(current => ({ ...current, temp: current.real }));
    }
    setLoading(false);
  };

  return (
    <div style={{ opacity: loading ? 0.75 : 1 }}>
      <SubmitTicketSupplierDropdown
        region={props.record.is_all_region ? undefined : props.record.region}
        value={value.temp}
        defaultValue={props.record.supplier}
        onChange={supplierId => changeRFISupplier(supplierId)}
      />
      <div style={{ marginTop: 8 }}>
        <RFISupplierEmailButton
          record={{
            ...props.record,
            supplier_id: value.temp,
          }}
        />
      </div>
    </div>
  );
};

const RFIBuyerEntitySelect = (props: { record: RFITicket }) => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    real: props.record.entity_id,
    temp: props.record.entity_id,
  });
  const [filters, setFilters] = useState({
    q: props.record.entity ? props.record.entity.name : '',
    page: 1,
  });

  // const marketplaces = useQuery<{
  //   items: RFIRegionMarketplace[];
  //   page: number;
  //   per_page: number;
  //   total: number;
  //   loading: boolean;
  // }>(
  //   ['ticketing-marketplace-region-entities', filters],
  //   async () => {
  //     try {
  //       const resp = await fetchAPI('rfi-ticketing/sme_marketplace/list', {
  //         method: 'POST',
  //         payload: {
  //           page: filters.page,
  //           per_page: 10,
  //           q: filters.q,
  //         },
  //       });

  //       return { ...resp, loading: false };
  //     } catch (error) {
  //       message.error('Failed get marketplace region entities');
  //       return {
  //         items: [],
  //         page: filters.page,
  //         per_page: 10,
  //         total: 0,
  //         loading: false,
  //       };
  //     }
  //   },
  //   {
  //     staleTime: 5 * 60 * 1000,
  //     placeholderData: {
  //       items: [],
  //       page: filters.page,
  //       per_page: 10,
  //       total: 0,
  //       loading: true,
  //     },
  //   }
  // );
  const buyerEntities = useQuery<{
    items: Array<{ id: number; name: string }>;
    page: number;
    per_page: number;
    total: number;
    loading: boolean;
  }>(
    ['entity/list-for-filter', filters],
    async () => {
      try {
        const resp = await fetchAPI('entity/list-for-filter', {
          method: 'POST',
          payload: {
            q: filters.q,
            page: filters.page,
            per_page: 10,
          },
        });

        return { ...resp, loading: false };
      } catch (error) {
        message.error('Failed get buyer entities');
        return {
          items: [],
          page: filters.page,
          per_page: 10,
          total: 0,
          loading: false,
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        items: [],
        page: filters.page,
        per_page: 10,
        total: 0,
        loading: true,
      },
    }
  );

  const changeRFIEntity = async (id: number) => {
    setValues(v => ({ ...v, real: id }));
    setLoading(true);
    try {
      const buyerEntity = buyerEntities.data.items?.find(
        item => item.id === id
      );
      if (!buyerEntity) {
        throw new Error('Not Found');
      }

      await updateRFITicket({
        id: props.record.id,
        entity_id: id,
      });
      setValues(v => ({ ...v, temp: id }));
    } catch (error) {
      message.error('Failed change RFI Entity');
      setValues(v => ({ ...v, real: v.temp }));
    }
    setLoading(false);
  };

  return (
    <Select
      defaultValue={values.real}
      style={{ width: '100%' }}
      onChange={val => changeRFIEntity(val)}
      loading={loading}
      placeholder="Select Buyer Entity"
      onSearch={debounce(val => {
        setFilters({ page: 1, q: val });
      }, 500)}
      showSearch
      filterOption={false}
      notFoundContent={
        buyerEntities.data.loading || buyerEntities.isLoading ? (
          <div style={{ padding: '10px 0', textAlign: 'center' }}>
            <LoadingOutlined />
          </div>
        ) : (
          undefined
        )
      }
    >
      {buyerEntities.data.items.map(item => {
        return (
          <Select.Option value={item.id} key={item.id}>
            {item.name}
          </Select.Option>
        );
      })}
    </Select>
  );
};

const RFIDatepicker = (props: {
  record: RFITicket;
  fieldKey: string;
  placeholder: string;
}) => {
  const recordValue = props.record[props.fieldKey];
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState({
    real: recordValue ? moment(recordValue) : null,
    temp: recordValue ? moment(recordValue) : null,
  });

  const changeRFIDate = async (date?: moment.Moment) => {
    setLoading(true);
    try {
      setValue(val => ({ ...val, temp: date }));
      await updateRFITicket({
        id: props.record.id,
        [props.fieldKey]: date
          ? date.format('YYYY-MM-DD') + 'T00:00:00+00:00'
          : '1990-01-01T00:00:00+00:00',
      });
      setValue({ real: date, temp: date });
    } catch (error) {
      message.error('Failed update ' + props.fieldKey);
      setValue(val => ({ ...val, temp: val.real }));
    }
    setLoading(false);
  };

  return (
    <DatePicker
      value={value.temp}
      placeholder={props.placeholder}
      onChange={date => changeRFIDate(date)}
      disabled={loading}
      suffixIcon={loading ? <LoadingOutlined /> : <CalendarOutlined />}
      format={'DD MMM YYYY'}
    />
  );
};

const RFIActionBy = (props: { record: RFITicket }) => {
  const user = useSelector((state: AppState) => state.user);
  const [value, setValue] = useState({
    real: null,
    temp: null,
  });
  const [loading, setLoading] = useState(false);
  const saUsers = useSAUsers(user);

  const assignUserToTicket = async newUserId => {
    setLoading(true);
    setValue(ui => ({ ...ui, temp: newUserId }));
    try {
      await updateRFITicket({
        id: props.record.id,
        action_by_id: newUserId,
      });
      setValue({ real: newUserId, temp: newUserId });
    } catch (error) {
      message.error('Failed assign staff');
      message.error(error.message || 'Error');
      setValue(val => ({ ...val, temp: val.real }));
    }
    setLoading(false);
  };

  useEffect(() => {
    const propsUserId = props.record.action_by_id || null;
    setValue({
      real: propsUserId,
      temp: propsUserId,
    });
  }, [props.record?.action_by_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Select
      style={{ width: '100%' }}
      value={value.temp || undefined}
      options={saUsers.data.list.map(user => ({
        label: user.full_name,
        value: user.id,
      }))}
      loading={saUsers.isLoading || loading}
      onSelect={val => assignUserToTicket(val)}
      placeholder="Select Staff"
      showSearch
      filterOption={(value, option) => {
        const userName = String(option.label).toLowerCase();
        return userName.includes(value.toLowerCase());
      }}
    />
  );
};

const RFITicketStatus = (props: { record: RFITicket }) => {
  const [value, setValue] = useState({
    real: null,
    temp: null,
  });
  const [loading, setLoading] = useState(false);

  const setTicketStatus = async newStatus => {
    setLoading(true);
    setValue(ui => ({ ...ui, temp: newStatus }));
    try {
      await updateRFITicket({
        id: props.record.id,
        status: newStatus,
      });
      setValue({ real: newStatus, temp: newStatus });
    } catch (error) {
      message.error('Failed update status');
      message.error(error.message || 'Error');
      setValue(val => ({ ...val, temp: val.real }));
    }
    setLoading(false);
  };

  useEffect(() => {
    const propsStatus = props.record.status || null;
    setValue({
      real: propsStatus,
      temp: propsStatus,
    });
  }, [props.record?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Select
      style={{ width: '100%' }}
      value={value.temp || undefined}
      options={[
        'Submitted',
        'Assigned',
        'Acknowledged',
        'Resolved',
        'InvoiceDisputed',
        'ProcessingInvoice',
        'InvoicePaid',
        'InvoiceRejected',
        'RFQInProgress',
        'RFQRejected',
        'RFQSuccess',
        'RFQInDispute',
        'Closed',
      ].map(status => ({
        label: (
          <Tag
            color={RFIStatusColor[status]?.bg}
            style={{ color: RFIStatusColor[status]?.text }}
          >
            {status}
          </Tag>
        ),
        value: status,
      }))}
      loading={loading}
      onSelect={val => setTicketStatus(val)}
      placeholder="Select Staff"
    />
  );
};

const RFINote = (props: { record: RFITicket }) => {
  const [note, setNote] = useState({
    temp: '',
    real: '',
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setNote({ real: props.record.comments, temp: props.record.comments });
  }, [props.record?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTicketNote = async newNote => {
    setLoading(true);
    try {
      await updateRFITicket({
        id: props.record.id,
        comments: newNote,
      });
      setNote({ real: newNote, temp: newNote });
      setEditing(false);
    } catch (error) {
      message.error('Failed save note');
      message.error(error.message || 'Error');
    }
    setLoading(false);
  };

  if (!editing) {
    return (
      <span className="ticket-note">
        {note.temp || '-'} <EditOutlined onClick={() => setEditing(true)} />
      </span>
    );
  }

  return (
    <div className="edit-ticket-note">
      <Input.TextArea
        value={note.temp}
        onChange={e => {
          e.persist();
          setNote(n => ({ ...n, temp: e.target?.value }));
        }}
        placeholder="Input comments"
        disabled={loading}
      />
      <div className="btn-container">
        <Button
          type="link"
          onClick={() => {
            setEditing(false);
            setNote(n => ({ ...n, temp: n.real }));
          }}
          disabled={loading}
          size="small"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          loading={loading}
          onClick={() => saveTicketNote(note.temp)}
          size="small"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export const RFIRequestMsg = (props: { msg: string }) => {
  const [parsed, setParsed] = useState(null);
  const [expand, setExpand] = useState({
    enabled: false,
    expand: false,
    oriHeight: -1,
  });
  const container = useRef<HTMLDivElement>();
  const setHeightContainer = () => {
    if (!container.current) {
      return;
    }

    const rect = container.current.getBoundingClientRect();
    if (rect.height > 150) {
      setExpand({
        enabled: true,
        expand: false,
        oriHeight: rect.height,
      });
    }
  };

  useEffect(() => {
    const splitLineBreak = (props.msg || '').split('\n');
    const parsedStr = splitLineBreak.reduce((el, lineStr, idx) => {
      const splitWhiteSpace = lineStr.split(' ');

      return (
        <>
          {!idx ? null : (
            <>
              {el}
              <br />
            </>
          )}
          {splitWhiteSpace.map(str => {
            const validLink: any = (sStr => {
              try {
                const valid = new URL(sStr);
                return valid;
              } catch (error) {
                return false;
              }
            })(str);

            if (validLink) {
              return (
                <>
                  {' '}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={validLink.href}
                  >
                    {str}
                  </a>
                </>
              );
            }

            return ` ${str}`;
          })}
        </>
      );
    }, <></>);

    setParsed(parsedStr);

    setTimeout(() => {
      setHeightContainer();
    }, 100);
  }, [props.msg]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ticket-request-msg">
      <div
        ref={container}
        className="request-msg"
        style={
          expand.enabled
            ? {
                height: expand.expand ? expand.oriHeight : 150,
                overflow: 'hidden',
              }
            : {}
        }
      >
        {parsed}
      </div>
      {expand.enabled && (
        <p
          className="show-more"
          onClick={() => setExpand(exp => ({ ...exp, expand: !exp.expand }))}
        >
          {!expand.expand ? 'Show More' : 'Hide'}
        </p>
      )}
    </div>
  );
};

const Wrapper = styled.div`
  .top-btn-container {
    margin-bottom: 15px;

    > * {
      margin-right: 10px;
    }

    .ant-btn-sm {
      > span {
        font-size: 12px;
      }
    }
  }

  .ant-table {
    th {
      font-weight: 600;
    }

    th,
    td {
      font-size: 12px;
    }

    tr:nth-child(odd) td {
      background-color: #dfe7ff;
    }
  }

  .ant-select {
    span.ant-select-selection-item,
    span.ant-select-selection-placeholder {
      font-size: 12px;
    }
  }

  .ant-picker .ant-picker-input input {
    font-size: 12px;
  }

  .company {
    p {
      margin: 0;

      &.email {
        font-size: 10px;
      }
    }
  }

  .attachment-card {
    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .ticket-note {
    white-space: pre-wrap;
  }

  .edit-ticket-note {
    textarea.ant-input {
      font-size: 12px;
      padding: 0 5px;
      margin: 0 0 10px;
    }

    .btn-container {
      display: flex;
      justify-content: space-between;

      .ant-btn {
        span {
          font-size: 12px;
          transform: translateY(-2px);
        }
      }
    }
  }

  .ticket-request-msg {
    p.show-more {
      text-align: right;
      font-weight: 600;

      &:hover {
        cursor: pointer;
      }
    }
  }
  .overdue-row {
    padding: 5px;
    background-color: rgb(207, 19, 34);
    color: #fff;
  }
`;

// Re-export from utils for backward compatibility
// Main implementation moved to utils/json_utils.ts
export { safelyParseJSON } from '../../utils/json_utils';

export default SubmittedTicketList;
